import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EVENT_TYPE_BACK_BUTTON } from 'utils/events';

/**
 * Back unificado cross-platform (web / PWA / WebView nativo).
 *
 * - Si se provee `onBack`, ejecuta ese callback.
 * - Si no, hace `navigate(-1)` — funciona en los 3 modos vía react-router
 *   (el WebView nativo sigue siendo una SPA en navegador embebido).
 *
 * Con `listenNative: true` suscribe el evento `EVENT_TYPE_BACK_BUTTON` que el
 * shell nativo dispara vía `window.backPressed()` (botón físico de Android),
 * de modo que el back nativo y el botón in-app comparten exactamente la misma lógica.
 *
 * @param {Function|null} onBack callback opcional de retroceso.
 * @param {{ listenNative?: boolean }} options
 * @returns {Function} goBack
 */
const useBack = (onBack = null, { listenNative = false } = {}) => {
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
    } else {
      navigate(-1);
    }
  }, [onBack, navigate]);

  useEffect(() => {
    if (!listenNative) return undefined;
    document.addEventListener(EVENT_TYPE_BACK_BUTTON, goBack);
    return () => {
      document.removeEventListener(EVENT_TYPE_BACK_BUTTON, goBack);
    };
  }, [listenNative, goBack]);

  return goBack;
};

export default useBack;
